/**
 * Server Wake-Up 상태 관리 모듈
 *
 * Fly.io auto-stop으로 서버가 절전 모드일 때
 * health check로 서버를 깨우고 상태를 UI에 전달
 */

export type WakeUpStatus = 'idle' | 'checking' | 'waking' | 'ready' | 'error';

interface WakeUpState {
  status: WakeUpStatus;
  message: string;
}

type Listener = (state: WakeUpState) => void;

let currentState: WakeUpState = { status: 'idle', message: '' };
const listeners = new Set<Listener>();

function setState(newState: WakeUpState) {
  currentState = newState;
  listeners.forEach((listener) => listener(currentState));
}

export function getWakeUpState(): WakeUpState {
  return currentState;
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** axios 인터셉터에서 cold start 재시도 시 호출 */
export function notifyColdStartRetry(retryCount: number, maxRetries: number) {
  const messages = [
    '자고 있던 서버를 깨우고 있습니다...',
    '서버가 하품하며 일어나는 중...',
    '거의 다 깼습니다! 조금만요...',
  ];
  const message = messages[Math.min(retryCount - 1, messages.length - 1)];
  setState({
    status: 'waking',
    message: `${message} (${retryCount}/${maxRetries})`,
  });
}

// ready → idle 전환 타이머 관리
let readyTimeoutId: ReturnType<typeof setTimeout> | null = null;

function clearReadyTimeout() {
  if (readyTimeoutId) {
    clearTimeout(readyTimeoutId);
    readyTimeoutId = null;
  }
}

/** axios 인터셉터에서 cold start 해소 시 호출 */
export function notifyColdStartResolved() {
  clearReadyTimeout();
  setState({ status: 'ready', message: '서버가 깨어났습니다! 어서오세요!' });
  readyTimeoutId = setTimeout(() => {
    if (currentState.status === 'ready') {
      setState({ status: 'idle', message: '' });
    }
    readyTimeoutId = null;
  }, 2500);
}

/** axios 인터셉터에서 cold start 최종 실패 시 호출 */
export function notifyColdStartFailed() {
  setState({
    status: 'error',
    message: '서버가 깊은 잠에 빠져있네요... 잠시 후 다시 시도해주세요.',
  });
}

// Health check 중복 방지
let healthCheckPromise: Promise<boolean> | null = null;

const BASE_URL =
  import.meta.env.VITE_API_URL || 'http://localhost:4000/api/v1';
const HEALTH_URL = BASE_URL.replace('/api/v1', '/health');

/**
 * 서버 health check 실행
 * 이미 실행 중이면 기존 Promise를 반환 (중복 방지)
 */
export async function checkServerHealth(): Promise<boolean> {
  if (healthCheckPromise) return healthCheckPromise;
  healthCheckPromise = performHealthCheck();
  try {
    return await healthCheckPromise;
  } finally {
    healthCheckPromise = null;
  }
}

async function performHealthCheck(): Promise<boolean> {
  const maxAttempts = 5;
  const progressMessages = [
    '자고 있던 서버를 깨우고 있습니다... zzZ',
    '서버가 기지개를 켜는 중...',
    '이불 걷고 일어나는 중... 조금만 기다려주세요!',
    '거의 다 준비됐습니다! 서버도 출근이 힘든가 봐요...',
    '마지막 세수 중... 곧 준비됩니다!',
  ];
  let lastErrorMsg = 'Unknown Connection Error';

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      const controller = new AbortController();
      // 첫 번째 시도는 서버가 깨어있는지 확인하기 위해 3초 타임아웃으로 빠르게 체크
      const timeoutId = setTimeout(() => controller.abort(), attempt === 0 ? 3000 : 10000);

      const response = await fetch(HEALTH_URL, {
        method: 'GET',
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (response.ok) {
        if (attempt === 0) {
          // 첫 시도에 성공 → 서버가 이미 깨어있음 → 조용히 처리 (배너 표시 안 함)
          setState({ status: 'idle', message: '' });
        } else {
          // 재시도 후 성공 → 완료 안내
          clearReadyTimeout();
          setState({ status: 'ready', message: '서버가 깨어났습니다! 어서오세요!' });
          readyTimeoutId = setTimeout(() => {
            if (currentState.status === 'ready') {
              setState({ status: 'idle', message: '' });
            }
            readyTimeoutId = null;
          }, 2000);
        }
        return true;
      } else {
        lastErrorMsg = `HTTP ${response.status} (${response.statusText || 'Response Error'})`;
      }
    } catch (err: any) {
      // 네트워크 에러 또는 타임아웃 → 계속 시도
      lastErrorMsg = err?.name === 'AbortError' 
        ? `Timeout (Attempt ${attempt + 1})` 
        : (err?.message || err?.toString() || 'Network Error');
    }

    // 첫 시도가 실패했을 때 비로소 'checking' 상태로 배너를 띄움
    if (attempt === 0) {
      setState({ status: 'checking', message: '서버가 깨어있는지 확인하는 중...' });
      // UI 렌더링 주기를 고려해 짧은 지연시간(100ms) 추가하여 배너 상태 갱신
      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    // 두 번째 시도부터 waking 상태 및 메시지 변경 표시
    setState({ status: 'waking', message: progressMessages[attempt] });

    // 대기: 2초, 3초, 4초, 5초, 5초
    const delay = Math.min(2000 + attempt * 1000, 5000);
    await new Promise((resolve) => setTimeout(resolve, delay));
  }

  setState({
    status: 'error',
    message: `서버가 깊은 잠에 빠져있네요... (상세: ${lastErrorMsg})`,
  });
  return false;
}
